<template>
  <div class="ws-after-sales-review">
    <div class="page-header">
      <div class="back-btn" @click="goBack">{{ $t('wholesaler.AfterSalesReview.line4_text') }}</div>
      <h1>{{ $t('wholesaler.AfterSalesReview.line5_text') }}</h1>
      <div class="sub">{{ detail.afterSalesNo || '' }}</div>
    </div>

    <div v-loading="loading" class="content">
      <!-- 客户申请信息 -->
      <div class="module">
        <div class="module-header"><span class="icon">📝</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line12_text') }}</span></div>
        <div class="module-body">
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line14_text') }}</span><span class="value">{{ detail.customerName || '-' }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line15_text') }}</span><span class="value mono" style="color:#457b9d;">{{ detail.orderNo }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line16_text') }}</span><span class="value">{{ reasonText(detail.reasonCode) }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line17_text') }}</span><span class="value" style="text-align:right;max-width:200px;">{{ detail.reasonText }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line18_text') }}</span><span class="value">{{ detail.customerExpect || '-' }}</span></div>
        </div>
      </div>

      <!-- 凭证图片 -->
      <div v-if="evidenceList.length > 0" class="module">
        <div class="module-header"><span class="icon">📸</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line24_text') }}</span></div>
        <div class="module-body">
          <div class="evidence-grid">
            <el-image v-for="(url, i) in evidenceList" :key="i" :src="getImageUrl(url)" :preview-src-list="evidenceList" class="evidence-img" fit="cover"></el-image>
          </div>
        </div>
      </div>

      <!-- 选择处理方案 -->
      <div class="module" style="border:2px solid #1890ff;">
        <div class="module-header" style="background:#e6f7ff;color:#1890ff;"><span class="icon">✏️</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line34_text') }}</span></div>
        <div class="module-body" style="padding:8px;">
          <div
            v-for="opt in resolutionOptions"
            :key="opt.value"
            class="resolution-option"
            :class="{ selected: form.resolutionType === opt.value }"
            @click="selectResolution(opt.value)"
          >
            <div class="r-icon">{{ opt.icon }}</div>
            <div class="r-info">
              <div class="r-title">{{ opt.label }}</div>
              <div class="r-desc">{{ opt.desc }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ⭐ 退回商品选择（退货退款/换货） -->
      <div v-if="showReturnItems" class="module" style="border:2px solid #fa8c16;">
        <div class="module-header" style="background:#fff7e6;color:#d46b08;"><span class="icon">📦</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line54_text') }}</span></div>
        <div class="module-body">
          <div class="item-row" v-for="item in applyItemsList" :key="item.orderItemId">
            <el-checkbox v-model="item.selected"></el-checkbox>
            <div class="item-info">
              <span class="item-name">{{ item.productName }}</span>
              <span class="item-qty">× {{ item.quantity }}</span>
            </div>
            <div class="item-edit">
              <span class="edit-label">{{ $t('wholesaler.AfterSalesReview.line63_text') }}</span>
              <el-input-number
                v-model="item.returnQty"
                :min="0"
                :max="item.quantity"
                :precision="2"
                :step="0.1"
                size="mini"
                :disabled="!item.selected"
              ></el-input-number>
            </div>
          </div>
        </div>
      </div>

      <!-- ⭐ 换出/补发商品选择（换货/补发） -->
      <div v-if="showResendItems" class="module" style="border:2px solid #722ed1;">
        <div class="module-header" style="background:#f9f0ff;color:#722ed1;"><span class="icon">📮</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line80_text') }}</span></div>
        <div class="module-body">
          <div class="item-row" v-for="(item, idx) in resendItems" :key="idx">
            <el-select v-model="item.productId" size="small" :placeholder="$t('wholesaler.AfterSalesReview.line83_placeholder')" style="width:160px;">
              <el-option
                v-for="p in orderProducts"
                :key="p.productId"
                :label="p.productName"
                :value="p.productId"
              ></el-option>
            </el-select>
            <span class="item-edit">
              <span class="edit-label">{{ $t('wholesaler.AfterSalesReview.line92_text') }}</span>
              <el-input-number v-model="item.quantity" :min="0.01" :precision="2" :step="0.1" size="mini" style="width:140px;"></el-input-number>
            </span>
            <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeResendItem(idx)" circle style="margin-left:4px;"></el-button>
          </div>
          <el-button type="text" @click="addResendItem" style="margin-top:4px;color:#722ed1;">{{ $t('wholesaler.AfterSalesReview.line97_text') }}</el-button>
        </div>
      </div>

      <!-- ⭐ 仓库选择 -->
      <div v-if="showWarehouseSelect" class="module">
        <div class="module-header"><span class="icon">🏠</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line103_text') }}</span></div>
        <div class="module-body">
          <div v-if="showReturnWarehouse" class="wh-select">
            <span class="wh-label">{{ $t('wholesaler.AfterSalesReview.line106_text') }}</span>
            <el-select v-model="form.returnWarehouseId" :placeholder="$t('wholesaler.AfterSalesReview.line107_placeholder')" size="small" style="width:200px;">
              <el-option
                v-for="wh in warehouses"
                :key="wh.id"
                :label="wh.warehouseName + (wh.isDefault ? ' (默认)' : '')"
                :value="wh.id"
              ></el-option>
            </el-select>
          </div>
          <div v-if="showResendWarehouse" class="wh-select" style="margin-top:8px;">
            <span class="wh-label">{{ $t('wholesaler.AfterSalesReview.line117_text') }}</span>
            <el-select v-model="form.resendWarehouseId" :placeholder="$t('wholesaler.AfterSalesReview.line118_placeholder')" size="small" style="width:200px;">
              <el-option
                v-for="wh in warehouses"
                :key="wh.id"
                :label="wh.warehouseName + (wh.isDefault ? ' (默认)' : '')"
                :value="wh.id"
              ></el-option>
            </el-select>
          </div>
        </div>
      </div>

      <!-- ⭐ 配送员选择 -->
      <div v-if="showDriverSelect" class="module">
        <div class="module-header"><span class="icon">🚚</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line132_text') }}</span></div>
        <div class="module-body">
          <div v-if="showPickupDriver" class="wh-select">
            <span class="wh-label">取件配送员{{ form.resolutionType === 'RETURN_REFUND' ? '（必填）' : '' }}：</span>
            <el-select v-model="form.pickupDriverId" :placeholder="$t('wholesaler.AfterSalesReview.line136_placeholder')" size="small" style="width:200px;">
              <el-option
                v-for="d in drivers"
                :key="d.id"
                :label="d.name + ' (' + d.phone + ') - ' + driverStatusText(d.status)"
                :value="d.id"
                :disabled="d.status === 'OFFLINE' || d.status === 'REST'"
              ></el-option>
            </el-select>
          </div>
          <div v-if="showResendDriver" class="wh-select" style="margin-top:8px;">
            <span class="wh-label">{{ $t('wholesaler.AfterSalesReview.line147_text') }}</span>
            <el-select v-model="form.resendDriverId" :placeholder="$t('wholesaler.AfterSalesReview.line148_placeholder')" size="small" style="width:200px;">
              <el-option
                v-for="d in drivers"
                :key="d.id"
                :label="d.name + ' (' + d.phone + ') - ' + driverStatusText(d.status)"
                :value="d.id"
                :disabled="d.status === 'OFFLINE' || d.status === 'REST'"
              ></el-option>
            </el-select>
          </div>
        </div>
      </div>

      <!-- 退款金额 -->
      <div v-if="showRefundAmount" class="module" :style="form.resolutionType === 'RETURN_REFUND' ? { border: '2px solid #ff4d4f' } : {}">
        <div class="module-header" :style="form.resolutionType === 'RETURN_REFUND' ? 'background:#fff2f0;color:#ff4d4f;' : ''">
          <span class="icon">💰</span>
          <span class="title">退款金额{{ form.resolutionType === 'RETURN_REFUND' ? '（必填）' : '' }}</span>
        </div>
        <div class="module-body">
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line168_text') }}</span><span class="value">S$ {{ estimateRefund.item }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.AfterSalesReview.line169_text') }}</span><span class="value">S$ {{ estimateRefund.gst }}</span></div>
          <div class="row" style="padding-top:12px;border-top:2px solid #f0f0f0;"><span class="label" style="font-weight:600;">{{ $t('wholesaler.AfterSalesReview.line170_text') }}</span><span class="value amount">S$ {{ estimateRefund.total }}</span></div>
          <div style="font-size:11px;color:#bbb;margin-top:6px;">{{ $t('wholesaler.AfterSalesReview.line171_text') }}</div>
          <el-input-number v-model="form.refundAmountCents" :min="0" :step="100" :precision="0" size="small" style="margin-top:8px;width:100%;" :placeholder="form.resolutionType === 'RETURN_REFUND' ? '退款金额（分），必填' : '退款金额（分）'"></el-input-number>
        </div>
      </div>

      <!-- 审核意见 -->
      <div class="module">
        <div class="module-header"><span class="icon">📝</span><span class="title">{{ $t('wholesaler.AfterSalesReview.line178_text') }}</span></div>
        <div class="module-body">
          <el-input v-model="form.reviewNote" type="textarea" :rows="2" :placeholder="$t('wholesaler.AfterSalesReview.line180_placeholder')" maxlength="500" show-word-limit></el-input>
          <div v-if="showReturnMethod" style="margin-top:8px;">
            <span style="font-size:13px;">{{ $t('wholesaler.AfterSalesReview.line182_text') }}</span>
            <el-radio-group v-model="form.returnMethod">
              <el-radio label="COURIER_PICKUP">{{ $t('wholesaler.AfterSalesReview.line184_text') }}</el-radio>
              <el-radio label="SELF_SEND">{{ $t('wholesaler.AfterSalesReview.line185_text') }}</el-radio>
            </el-radio-group>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="bottom-actions">
      <el-button @click="goBack" style="flex:1;">{{ $t('wholesaler.AfterSalesReview.line194_text') }}</el-button>
      <el-button type="primary" @click="submitReview" :loading="submitting" style="flex:2;">{{ $t('wholesaler.AfterSalesReview.line195_text') }}</el-button>
    </div>
  </div>
</template>

<script>
import { getImageUrl } from '@/utils/image';
import {
  getAfterSalesDetail,
  reviewAfterSales,
  getAvailableDrivers,
  getAvailableWarehouses
} from '@/services/afterSales';

export default {
  name: 'WholesalerAfterSalesReview',
  data() {
    return {
      loading: false,
      submitting: false,
      detail: {},
      form: {
        resolutionType: '',
        reviewNote: '',
        refundAmountCents: 0,
        returnMethod: 'COURIER_PICKUP',
        returnWarehouseId: null,
        resendWarehouseId: null,
        pickupDriverId: null,
        resendDriverId: null
      },
      resolutionOptions: [
        { value: 'RETURN_REFUND', icon: '↩️', label: '退货退款', desc: '安排取件 + 质检 + 退款' },
        { value: 'EXCHANGE', icon: '🔄', label: '换货', desc: '取回原商品 + 发出新商品' },
        { value: 'RESEND', icon: '📦', label: '补发', desc: '不取回，直接再发一次' },
        { value: 'PARTIAL_REFUND', icon: '💸', label: '部分退款', desc: '不退商品，退部分款项' },
        { value: 'NO_ACTION', icon: '🚫', label: '不处理', desc: '拒绝此申请，需说明理由' }
      ],
      drivers: [],
      warehouses: [],
      resendItems: []
    };
  },
  computed: {
    evidenceList() {
      try { return this.detail.evidenceUrls ? JSON.parse(this.detail.evidenceUrls) : []; }
      catch { return []; }
    },
    applyItemsList() {
      try {
        const items = this.detail.applyItems ? JSON.parse(this.detail.applyItems) : [];
        return items.map(item => ({
          ...item,
          selected: true,
          returnQty: item.quantity
        }));
      } catch { return []; }
    },
    orderProducts() {
      try {
        const items = this.detail.applyItems ? JSON.parse(this.detail.applyItems) : [];
        return items.map(item => ({ productId: item.productId || item.orderItemId, productName: item.productName }));
      } catch { return []; }
    },
    showRefundAmount() {
      return ['RETURN_REFUND', 'PARTIAL_REFUND'].includes(this.form.resolutionType);
    },
    showReturnMethod() {
      // ⭐ 简化流程（2026-05-11）：退货退款不再显示退货方式（固定为配送员取件）
      return ['EXCHANGE'].includes(this.form.resolutionType);
    },
    showReturnItems() {
      // ⭐ 简化流程（2026-05-11）：退货退款不再在审核时选择退回商品
      // 退回商品在处理页面统一处理
      return ['EXCHANGE'].includes(this.form.resolutionType);
    },
    showResendItems() {
      return ['EXCHANGE', 'RESEND'].includes(this.form.resolutionType);
    },
    showWarehouseSelect() {
      return this.showReturnItems || this.showResendItems;
    },
    showReturnWarehouse() {
      // ⭐ 简化流程（2026-05-11）：退货退款不再在审核时选择退货仓库
      return ['EXCHANGE'].includes(this.form.resolutionType);
    },
    showResendWarehouse() {
      return ['EXCHANGE', 'RESEND'].includes(this.form.resolutionType);
    },
    showDriverSelect() {
      // ⭐ 简化流程：退货退款必须选配送员，换货/补发可选发货配送员
      return this.showPickupDriver || this.showResendItems;
    },
    showPickupDriver() {
      // ⭐ 简化流程（2026-05-11）：退货退款必须安排取件配送员
      return ['RETURN_REFUND', 'EXCHANGE'].includes(this.form.resolutionType) && this.form.returnMethod === 'COURIER_PICKUP';
    },
    showResendDriver() {
      return ['EXCHANGE', 'RESEND'].includes(this.form.resolutionType);
    },
    estimateRefund() {
      const refundAmount = this.form.refundAmountCents || this.detail.refundAmount || 0;
      const gstRate = 0.09;
      const itemRefund = Math.round(refundAmount / (1 + gstRate));
      const gstRefund = refundAmount - itemRefund;
      return {
        item: (itemRefund / 100).toFixed(2),
        gst: (gstRefund / 100).toFixed(2),
        total: (refundAmount / 100).toFixed(2)
      };
    }
  },
  created() { this.loadDetail(); },
  methods: {
    getImageUrl,
    async loadDetail() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        this.detail = await getAfterSalesDetail(id);
        
        // 默认选择退货退款
        this.form.resolutionType = 'RETURN_REFUND';
        
        // 默认全额退款：从订单商品计算总金额
        let totalAmount = 0;
        if (this.detail.orderItems && Array.isArray(this.detail.orderItems)) {
          totalAmount = this.detail.orderItems.reduce((sum, item) => {
            return sum + (item.subtotal || 0);
          }, 0);
        } else if (this.detail.applyItems) {
          try {
            const items = JSON.parse(this.detail.applyItems);
            totalAmount = items.reduce((sum, item) => {
              return sum + ((item.subtotal || item.unitPrice * item.quantity) || 0);
            }, 0);
          } catch (e) {
            console.error('解析 applyItems 失败:', e);
          }
        }
        
        // 转换为分（如果已经是分则不转换）
        if (totalAmount > 0) {
          this.form.refundAmountCents = totalAmount > 10000 
            ? Math.round(totalAmount) 
            : Math.round(totalAmount * 100);
        } else if (this.detail.refundAmount && this.detail.refundAmount > 0) {
          this.form.refundAmountCents = this.detail.refundAmount;
        }
        
        // 提前加载仓库和配送员
        await Promise.all([this.loadWarehouses(), this.loadDrivers()]);
        if (this.warehouses.length > 0) {
          const defaultWh = this.warehouses.find(w => w.isDefault) || this.warehouses[0];
          this.form.returnWarehouseId = defaultWh.id;
          this.form.resendWarehouseId = defaultWh.id;
        }
      } catch (e) {
        this.$message.error('加载失败: ' + (e.message || e));
      } finally { this.loading = false; }
    },
    async loadWarehouses() {
      try {
        this.warehouses = await getAvailableWarehouses();
      } catch (e) {
        this.warehouses = [];
      }
    },
    async loadDrivers() {
      try {
        this.drivers = await getAvailableDrivers();
      } catch (e) {
        this.drivers = [];
      }
    },
    selectResolution(value) {
      this.form.resolutionType = value;
    },
    addResendItem() {
      this.resendItems.push({ productId: null, quantity: 1 });
    },
    removeResendItem(idx) {
      this.resendItems.splice(idx, 1);
    },
    goBack() { this.$router.back(); },
    reasonText(code) { const m = { DAMAGED:'商品破损', WRONG_ITEM:'发错货了', NOT_RECEIVED:'没收到货', WRONG_SPEC:'规格不对', SHORT_DELIVERY:'少发了', DONT_WANT:'不想要了', OTHER:'其他' }; return m[code] || code; },
    driverStatusText(status) {
      const m = { IDLE:'空闲', DELIVERING:'配送中', REST:'休息', OFFLINE:'离线' };
      return m[status] || status;
    },
    async submitReview() {
      if (!this.form.resolutionType) {
        this.$message.warning('请选择处理方案');
        return;
      }
      if (this.form.resolutionType === 'NO_ACTION' && !this.form.reviewNote) {
        this.$message.warning('选择"不处理"时必须填写审核意见');
        return;
      }
      // ⭐ 退货退款：必须选择配送员且设置退款金额
      if (this.form.resolutionType === 'RETURN_REFUND') {
        if (!this.form.pickupDriverId) {
          this.$message.warning('退货退款必须安排取件配送员');
          return;
        }
        if (!this.form.refundAmountCents || this.form.refundAmountCents <= 0) {
          this.$message.warning('退货退款必须设置退款金额');
          return;
        }
      }
      // 退回商品校验
      if (this.showReturnItems) {
        const selectedItems = this.applyItemsList.filter(i => i.selected && i.returnQty > 0);
        if (selectedItems.length === 0) {
          this.$message.warning('请至少选择一个退回商品');
          return;
        }
      }
      // 换出/补发商品校验
      if (this.showResendItems) {
        const validItems = this.resendItems.filter(i => i.productId && i.quantity > 0);
        if (validItems.length === 0) {
          this.$message.warning('请至少添加一个换出/补发商品');
          return;
        }
      }
      this.submitting = true;
      try {
        // 构建退回商品 JSON
        let returnItemsJson = null;
        if (this.showReturnItems) {
          const selectedItems = this.applyItemsList.filter(i => i.selected && i.returnQty > 0);
          if (selectedItems.length > 0) {
            returnItemsJson = JSON.stringify(selectedItems.map(i => ({
              orderItemId: i.orderItemId,
              productId: i.productId,
              productName: i.productName,
              quantity: i.returnQty
            })));
          }
        }
        // 构建换出/补发商品 JSON
        let resendItemsJson = null;
        if (this.showResendItems) {
          const validItems = this.resendItems.filter(i => i.productId && i.quantity > 0);
          if (validItems.length > 0) {
            resendItemsJson = JSON.stringify(validItems.map(i => ({
              productId: i.productId,
              productName: i.productName,
              quantity: i.quantity
            })));
          }
        }
        await reviewAfterSales(this.detail.id, {
          resolutionType: this.form.resolutionType,
          reviewNote: this.form.reviewNote,
          refundAmount: ['RETURN_REFUND', 'PARTIAL_REFUND'].includes(this.form.resolutionType) ? this.form.refundAmountCents : undefined,
          returnMethod: this.showReturnMethod ? this.form.returnMethod : undefined,
          returnItems: returnItemsJson,
          returnWarehouseId: this.showReturnWarehouse ? this.form.returnWarehouseId : undefined,
          resendItems: resendItemsJson,
          resendWarehouseId: this.showResendWarehouse ? this.form.resendWarehouseId : undefined,
          pickupDriverId: this.showPickupDriver ? this.form.pickupDriverId : undefined,
          resendDriverId: this.showResendDriver ? this.form.resendDriverId : undefined
        });
        this.$message.success('审核完成');
        this.$router.push(`/wholesaler/after-sales/${this.detail.id}`);
      } catch (e) {
        this.$message.error('审核失败: ' + (e.message || e));
      } finally { this.submitting = false; }
    }
  }
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #1d3557, #457b9d); color: #fff; padding: calc(16px + env(safe-area-inset-top, 0px)); }
.back-btn { font-size: 12px; opacity: 0.8; cursor: pointer; margin-bottom: 4px; }
.back-btn:hover { opacity: 1; }
.page-header h1 { font-size: 17px; }
.page-header .sub { font-size: 12px; opacity: 0.8; }

.content { max-width: 600px; margin: 0 auto; padding-bottom: 60px; background: #f5f5f5; min-height: calc(100vh - 80px); }

.module { margin: 8px; background: #fff; border-radius: 8px; overflow: hidden; }
.module-header { display: flex; align-items: center; padding: 12px 12px 8px; gap: 6px; border-bottom: 1px solid #f0f0f0; }
.module-header .icon { font-size: 16px; }
.module-header .title { font-size: 14px; font-weight: 600; flex: 1; }
.module-body { padding: 12px; }

.row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 13px; }
.row .label { color: #999; flex-shrink: 0; }
.row .value { font-weight: 500; text-align: right; }
.row + .row { border-top: 1px dashed #f0f0f0; }
.row .value.mono { font-family: monospace; font-size: 12px; }
.row .value.amount { color: #ff4d4f; font-size: 15px; font-weight: 700; }

.evidence-grid { display: flex; gap: 8px; flex-wrap: wrap; }
.evidence-img { width: 80px; height: 80px; border-radius: 6px; }

/* 处理方案选择 */
.resolution-option {
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.resolution-option:hover { border-color: #457b9d; }
.resolution-option.selected { border-color: #1890ff; background: #e6f7ff; }
.r-icon { font-size: 24px; flex-shrink: 0; }
.r-info { flex: 1; }
.r-title { font-size: 14px; font-weight: 500; }
.r-desc { font-size: 12px; color: #999; margin-top: 2px; }

/* ⭐ 商品列表 */
.item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px dashed #f0f0f0;
}
.item-row:last-child { border-bottom: none; }
.item-info { flex: 1; }
.item-name { font-size: 13px; font-weight: 500; }
.item-qty { font-size: 12px; color: #999; margin-left: 4px; }
.edit-label { font-size: 12px; color: #666; margin-right: 4px; }

/* 仓库/配送员选择 */
.wh-select { display: flex; align-items: center; }
.wh-label { font-size: 13px; color: #666; margin-right: 8px; white-space: nowrap; }

.bottom-actions { position: sticky; bottom: 0; background: #fff; border-top: 1px solid #e8e8e8; padding: 12px; display: flex; gap: 8px; }
</style>
